/**
 * Triage — operator productivity surface for the discovery → article
 * pipeline. One tab per active exchange (today: only ASX). Scoped to
 * the last 2 trading days; older items are managed via the Articles
 * page per-article controls.
 *
 * Promote = force a previously-skipped scan into the article
 * generation path (enqueue source_announcement or process job).
 * Demote = move an announcement's articles back to draft, optionally
 * queueing unpublish jobs for any currently-live article. Demote is
 * reversible — archive/delete is the operator's separate call.
 */

import { useCallback, useMemo, useState } from 'react';
import { CoverageChainPanel } from '../components/CoverageChainPanel.js';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  X,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';

import { DiscoveryTriagePanel } from '../components/DiscoveryTriagePanel.js';
import HeldEventsPanel from '../components/HeldEventsPanel.js';
import {
  promoteScan,
  demoteAnnouncement,
  getTriageSelected,
  type TriageRow,
  type PromoteResponse,
  type DemoteResponse,
  type SelectedItem,
} from '../lib/api/triage.js';
import { Modal, ModalBody, ModalFooter } from '../components/ui/modal.js';
import { SUBSTANCE_LABEL, substanceBadgeClass } from '../lib/substance.js';

type Exchange = 'ASX';

const EXCHANGES: { id: Exchange; label: string; description: string }[] = [
  {
    id: 'ASX',
    label: 'ASX',
    description: 'Australian Securities Exchange — broad-scan pipeline.',
  },
];

interface DemoteConfirm {
  scanIds: string[];
  affectedRows: TriageRow[];
  unpublish: boolean;
}

type ViewMode = 'skipped' | 'selected';

export function TriagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeExchange = ((searchParams.get('exchange') as Exchange) || 'ASX');
  const viewMode = ((searchParams.get('view') as ViewMode) || 'skipped');
  const setViewMode = (m: ViewMode) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', m);
    setSearchParams(next);
  };
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Snapshot of every visible row keyed by scan_id, so the action bar can
  // resolve announcement_ids and currently-published states without
  // re-reading from the panel.
  const [rowIndex, setRowIndex] = useState<Map<string, TriageRow>>(new Map());
  const [unpublishOnDemote, setUnpublishOnDemote] = useState(false);
  const [demoteConfirm, setDemoteConfirm] = useState<DemoteConfirm | null>(null);
  const queryClient = useQueryClient();

  const setActiveExchange = (ex: Exchange) => {
    setSearchParams({ exchange: ex });
    setSelectedIds(new Set());
  };

  const toggleSelected = useCallback((scanId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId);
      else next.add(scanId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback((scanIds: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = scanIds.length > 0 && scanIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of scanIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of scanIds) next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const reportRows = useCallback((rows: TriageRow[]) => {
    setRowIndex(new Map(rows.map((r) => [r.scan_id, r])));
  }, []);

  const promoteMutation = useMutation({
    mutationFn: async (scanIds: string[]) => {
      const results = await Promise.allSettled(
        scanIds.map((id) => promoteScan(id)),
      );
      const summary = {
        queued: 0,
        reprocessing: 0,
        noop: 0,
        failed: 0,
        errors: [] as string[],
      };
      for (const r of results) {
        if (r.status === 'rejected') {
          summary.failed += 1;
          summary.errors.push(extractErrorMessage(r.reason));
          continue;
        }
        const status = (r.value as PromoteResponse).status;
        if (status === 'queued') summary.queued += 1;
        else if (status === 'reprocessing') summary.reprocessing += 1;
        else summary.noop += 1;
      }
      return summary;
    },
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: ['triage-today'] });
      clearSelection();
      const parts: string[] = [];
      if (summary.queued) parts.push(`Promoted ${summary.queued}`);
      if (summary.reprocessing) parts.push(`re-running ${summary.reprocessing}`);
      if (summary.noop) parts.push(`skipped ${summary.noop}`);
      if (summary.failed) parts.push(`failed ${summary.failed}`);
      const message = parts.length ? parts.join(' · ') : 'No changes';
      if (summary.failed) {
        const sample = summary.errors.slice(0, 3).join('\n');
        alert(`${message}\n\n${sample}`);
      } else {
        alert(message);
      }
    },
  });

  const demoteMutation = useMutation({
    mutationFn: async ({
      scanIds, unpublish,
    }: { scanIds: string[]; unpublish: boolean }) => {
      const announcementIds: string[] = [];
      for (const sid of scanIds) {
        const row = rowIndex.get(sid);
        if (!row) continue;
        const annId = row.articles.find((a) => a.announcement_id)?.announcement_id;
        if (annId) announcementIds.push(annId);
      }
      const unique = Array.from(new Set(announcementIds));
      const results = await Promise.allSettled(
        unique.map((id) => demoteAnnouncement(id, { unpublish })),
      );
      const summary = {
        demoted: 0,
        noop: 0,
        failed: 0,
        demotedToDraftTotal: 0,
        unpublishJobsTotal: 0,
        errors: [] as string[],
      };
      for (const r of results) {
        if (r.status === 'rejected') {
          summary.failed += 1;
          summary.errors.push(extractErrorMessage(r.reason));
          continue;
        }
        const v = r.value as DemoteResponse;
        if (v.status === 'demoted') summary.demoted += 1;
        else summary.noop += 1;
        summary.demotedToDraftTotal += v.demoted_to_draft;
        summary.unpublishJobsTotal += v.unpublish_jobs_queued;
      }
      return { summary, totalAttempted: unique.length };
    },
    onSuccess: ({ summary, totalAttempted }) => {
      queryClient.invalidateQueries({ queryKey: ['triage-today'] });
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      clearSelection();
      const parts: string[] = [];
      parts.push(`${totalAttempted} announcement(s) processed`);
      if (summary.demotedToDraftTotal) parts.push(`${summary.demotedToDraftTotal} → draft`);
      if (summary.unpublishJobsTotal) parts.push(`${summary.unpublishJobsTotal} unpublish queued`);
      if (summary.noop) parts.push(`${summary.noop} no-op`);
      if (summary.failed) parts.push(`failed ${summary.failed}`);
      const message = parts.join(' · ');
      if (summary.failed) {
        const sample = summary.errors.slice(0, 3).join('\n');
        alert(`${message}\n\n${sample}`);
      } else {
        alert(message);
      }
    },
  });

  const selectionStats = useMemo(() => {
    const rows: TriageRow[] = [];
    for (const id of selectedIds) {
      const r = rowIndex.get(id);
      if (r) rows.push(r);
    }
    const withAnnouncement = rows.filter(
      (r) => r.articles.some((a) => a.announcement_id) || r.articles.length > 0,
    );
    const allPublished = rows.length > 0
      && rows.every((r) => r.articles.length > 0 && r.articles.every((a) => a.publish_status === 'published'));
    const anyLive = rows.some(
      (r) => r.articles.some((a) => a.publish_status === 'published'),
    );
    return {
      rows,
      promoteEligible: rows.filter(
        (r) => !(r.articles.length > 0 && r.articles.every((a) => a.publish_status === 'published')),
      ).length,
      demoteEligible: withAnnouncement.length,
      anyLive,
      allPublished,
    };
  }, [selectedIds, rowIndex]);

  const onPromoteRow = useCallback((row: TriageRow) => {
    if (!confirm(`Promote scan for ${row.ticker} — "${truncate(row.title, 60)}"?`)) return;
    promoteMutation.mutate([row.scan_id]);
  }, [promoteMutation]);

  const onDemoteRow = useCallback((row: TriageRow) => {
    const live = row.articles.some((a) => a.publish_status === 'published');
    if (live && !unpublishOnDemote) {
      // Force the operator through the confirmation modal to opt into unpublish
      setDemoteConfirm({
        scanIds: [row.scan_id],
        affectedRows: [row],
        unpublish: true,
      });
      return;
    }
    if (!confirm(`Demote ${row.ticker} — articles back to draft?${live ? ' Live articles will also be unpublished.' : ''}`)) return;
    demoteMutation.mutate({
      scanIds: [row.scan_id],
      unpublish: live ? true : unpublishOnDemote,
    });
  }, [demoteMutation, unpublishOnDemote]);

  const handleBulkDemote = () => {
    const scanIds = Array.from(selectedIds);
    const rows = scanIds
      .map((id) => rowIndex.get(id))
      .filter((r): r is TriageRow => !!r);
    const live = rows.some((r) => r.articles.some((a) => a.publish_status === 'published'));
    if (live && !unpublishOnDemote) {
      setDemoteConfirm({
        scanIds,
        affectedRows: rows,
        unpublish: true,
      });
      return;
    }
    if (!confirm(`Demote ${scanIds.length} scan(s)? Articles back to draft.${live ? ' Live articles will also be unpublished.' : ''}`)) return;
    demoteMutation.mutate({ scanIds, unpublish: live ? true : unpublishOnDemote });
  };

  const handleBulkPromote = () => {
    if (!confirm(`Promote ${selectedIds.size} scan(s)?`)) return;
    promoteMutation.mutate(Array.from(selectedIds));
  };

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-grey-900">Triage</h1>
          <p className="text-sm text-grey-600 mt-1">
            Overrule the discovery pipeline. Promote skipped scans, demote queued
            ones. Last two trading days only — older content is managed from the
            Articles page.
          </p>
        </div>
      </header>

      <div className="flex border-b border-grey-200 gap-1">
        {EXCHANGES.map((ex) => {
          const active = activeExchange === ex.id;
          return (
            <button
              key={ex.id}
              onClick={() => setActiveExchange(ex.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
              }`}
              title={ex.description}
            >
              {ex.label}
            </button>
          );
        })}
        <span className="ml-auto flex border-l border-grey-200 pl-3">
          <button
            onClick={() => setViewMode('skipped')}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              viewMode === 'skipped'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-grey-500 hover:text-grey-700'
            }`}
          >
            Discovery scans
          </button>
          <button
            onClick={() => setViewMode('selected')}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              viewMode === 'selected'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-grey-500 hover:text-grey-700'
            }`}
            title="What the planner selected today, and from which sources"
          >
            Editorial picks
          </button>
        </span>
      </div>

      {viewMode === 'skipped' && <HeldEventsPanel />}

      {viewMode === 'skipped' ? (
        <DiscoveryTriagePanel
          key={activeExchange}
          exchange={activeExchange}
          days={2}
          selectionEnabled
          selectedIds={selectedIds}
          onToggleSelected={toggleSelected}
          onSelectAllVisible={selectAllVisible}
          onPromoteRow={onPromoteRow}
          onDemoteRow={onDemoteRow}
          onRowsChange={reportRows}
          groupedByDate
        />
      ) : (
        <SelectedPanel />
      )}

      {selectedIds.size > 0 && (
        <div className="sticky bottom-4 z-30">
          <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg flex-wrap shadow-md">
            <span className="text-sm font-medium text-primary-700">
              {selectedIds.size} selected
            </span>
            <button
              onClick={handleBulkPromote}
              disabled={promoteMutation.isPending || selectionStats.promoteEligible === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={selectionStats.promoteEligible === 0 ? 'All selected scans already published' : `Promote ${selectionStats.promoteEligible}`}
            >
              <ArrowUpCircle className="h-3.5 w-3.5" />
              Promote{selectionStats.promoteEligible > 0 ? ` (${selectionStats.promoteEligible})` : ''}
            </button>
            <label className="flex items-center gap-1.5 text-xs text-grey-700 select-none">
              <input
                type="checkbox"
                checked={unpublishOnDemote}
                onChange={(e) => setUnpublishOnDemote(e.target.checked)}
              />
              Also unpublish live articles
            </label>
            <button
              onClick={handleBulkDemote}
              disabled={demoteMutation.isPending || selectionStats.demoteEligible === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={selectionStats.demoteEligible === 0 ? 'No selected scan has an announcement' : `Demote ${selectionStats.demoteEligible}`}
            >
              <ArrowDownCircle className="h-3.5 w-3.5" />
              Demote{selectionStats.demoteEligible > 0 ? ` (${selectionStats.demoteEligible})` : ''}
            </button>
            <button
              onClick={clearSelection}
              className="ml-auto text-xs text-grey-600 hover:text-grey-900 inline-flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
        </div>
      )}

      {demoteConfirm && (
        <DemoteConfirmModal
          confirm={demoteConfirm}
          onCancel={() => setDemoteConfirm(null)}
          onConfirm={() => {
            const { scanIds, unpublish } = demoteConfirm;
            setDemoteConfirm(null);
            setUnpublishOnDemote(true);
            demoteMutation.mutate({ scanIds, unpublish });
          }}
        />
      )}

      {/* T-4: the coverage path. Fast-lane groups are SKIPPED by the broad
          scan, so their filings never appear in the table above — the least
          observable filings in the system were the ones being paid for. */}
      <CoverageChainPanel />
    </div>
  );
}

function DemoteConfirmModal({
  confirm, onCancel, onConfirm,
}: {
  confirm: DemoteConfirm;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const liveArticles = confirm.affectedRows.flatMap((r) =>
    r.articles
      .filter((a) => a.publish_status === 'published')
      .map((a) => ({ row: r, article: a })),
  );
  return (
    <Modal onClose={onCancel} size="lg">
      <ModalBody>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-grey-900">
            Demote with unpublish?
          </h2>
        </div>
        <p className="text-sm text-grey-700 mb-4">
          The selection includes {liveArticles.length} live article
          {liveArticles.length === 1 ? '' : 's'}. Confirming will queue
          unpublish jobs and move articles back to <code className="font-mono">draft</code>.
          Archive / delete is a separate step from the Articles page.
        </p>
        <ul className="text-xs text-grey-700 space-y-1 max-h-48 overflow-y-auto border border-grey-200 rounded p-2 bg-grey-50">
          {liveArticles.map(({ row, article }) => (
            <li key={article.article_id}>
              <span className="font-mono text-grey-500">{row.ticker}</span>{' '}
              · {article.title || article.article_id}
            </li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm rounded-md border border-grey-300 text-grey-700 hover:bg-grey-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700"
        >
          Demote &amp; unpublish
        </button>
      </ModalFooter>
    </Modal>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const e = err as { response?: { data?: { detail?: unknown }; status?: number } };
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message: unknown }).message);
    }
    if (e.response?.status) return `HTTP ${e.response.status}`;
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

// ============================================================================
// Selected stories panel — Phase 2 visibility
// ============================================================================

function SelectedPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['triage-selected'],
    queryFn: () => getTriageSelected(),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return <div className="text-sm text-grey-500 py-8 text-center">Loading…</div>;
  }
  if (error) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded p-3 text-sm text-danger-700">
        Failed to load selected stories: {extractErrorMessage(error)}
      </div>
    );
  }
  if (!data || data.items.length === 0) {
    return (
      <div className="text-sm text-grey-500 py-12 text-center">
        No selected stories yet today. Plan items appear here as the day-scoped triage runs.
      </div>
    );
  }

  // Group by entity for the operator view: one card per entity, each
  // showing all the plan items that ran for that entity today.
  const byEntity = new Map<string, { entity_company_code: string; entity_company_name: string; items: SelectedItem[] }>();
  for (const it of data.items) {
    const key = it.entity_id || it.entity_company_code || 'unrouted';
    const existing = byEntity.get(key);
    if (existing) {
      existing.items.push(it);
    } else {
      byEntity.set(key, {
        entity_company_code: it.entity_company_code || 'UNK',
        entity_company_name: it.entity_company_name || '',
        items: [it],
      });
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-grey-600">
        {data.count} plan item{data.count === 1 ? '' : 's'} today across{' '}
        {byEntity.size} entit{byEntity.size === 1 ? 'y' : 'ies'}. Each card shows
        the source bundle the article was generated from, with doc-form and
        editorial-substance signals so you can audit the anchor pick.
      </p>
      {Array.from(byEntity.values()).map((group) => (
        <div
          key={group.entity_company_code}
          className="bg-surface border border-grey-200 rounded-xl shadow-sm p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono font-semibold text-grey-900 text-sm">
              {group.entity_company_code}
            </span>
            <span className="text-sm text-grey-600">{group.entity_company_name}</span>
            <span className="ml-auto text-xs text-grey-400">
              {group.items.length} plan item{group.items.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="space-y-3">
            {group.items.map((item) => (
              <SelectedItemCard key={item.item_id} item={item} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SelectedItemCard({ item }: { item: SelectedItem }) {
  const anchor = item.sources.find((s) => s.announcement_id === item.primary_announcement_id) || item.sources[0];
  const supporting = item.sources.filter((s) => s.announcement_id !== item.primary_announcement_id);
  return (
    <div className="border border-grey-100 rounded-lg p-3 bg-grey-50">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {item.article_id ? (
              <Link
                to={`/articles/${item.article_id}`}
                className="text-sm font-semibold text-primary-700 hover:underline"
              >
                {item.article_title || item.primary_title}
              </Link>
            ) : (
              <span className="text-sm font-semibold text-grey-700">
                {item.primary_title}
              </span>
            )}
            {item.article_publish_status && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                  item.article_publish_status === 'published'
                    ? 'bg-success-100 text-success-700'
                    : item.article_publish_status === 'review_blocked'
                    ? 'bg-warning-100 text-warning-700'
                    : 'bg-grey-100 text-grey-600'
                }`}
              >
                {item.article_publish_status}
              </span>
            )}
            {item.article_auto_published && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-grey-200 text-grey-700"
                title="Published automatically"
              >
                {item.article_rewrite_count > 0
                  ? `auto v${item.article_rewrite_count + 1}`
                  : 'auto'}
              </span>
            )}
            {item.article_edited && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700"
                title="Operator edited"
              >
                edited
              </span>
            )}
            {item.is_secondary && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                title="Secondary editorial angle"
              >
                secondary
              </span>
            )}
          </div>
          <div className="text-xs text-grey-500 flex items-center gap-2 flex-wrap">
            <span>{item.product_name}</span>
            {item.target_name && <span>→ {item.target_name}</span>}
            {item.persona_slug && <span>· {item.persona_slug}</span>}
            <span>· {format(new Date(item.plan_created_at), 'HH:mm')}</span>
          </div>
        </div>
      </div>
      {item.triage_rationale && (
        <details className="mt-2">
          <summary className="text-[10px] font-semibold text-grey-500 uppercase cursor-pointer">
            Triage rationale
          </summary>
          <p className="mt-1 text-xs text-grey-700">{item.triage_rationale}</p>
        </details>
      )}
      <div className="mt-2 border-t border-grey-200 pt-2">
        <p className="text-[10px] font-semibold text-grey-500 uppercase mb-1">
          Sources used ({item.sources.length})
        </p>
        <ul className="space-y-1">
          {anchor && (
            <SourceRow s={anchor} isAnchor productType={item.product_type} />
          )}
          {supporting.map((s) => (
            <SourceRow
              key={s.announcement_id}
              s={s}
              productType={item.product_type}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

// Anchor-badge styling per route type. The "anchor" label has always
// meant "primary source for this plan item" — but operators read it as
// "editorial anchor pick" and grow alarmed when a substance=1 procedural
// filing wears the badge. The signal/activity/synthesis routes have
// always passed those filings through cleanly; this just makes the
// route surface in the UI.
function anchorBadgeStyle(productType: string | null | undefined): { label: string; cls: string } {
  const t = (productType || '').toLowerCase();
  if (t === 'article') {
    return { label: 'editorial anchor', cls: 'bg-primary-100 text-primary-700' };
  }
  if (t === 'intelligence_signal') {
    return { label: 'signal source', cls: 'bg-grey-200 text-grey-700' };
  }
  if (t === 'activity_item') {
    return { label: 'brief source', cls: 'bg-amber-100 text-amber-700' };
  }
  if (t === 'synthesis') {
    return { label: 'synthesis source', cls: 'bg-purple-100 text-purple-700' };
  }
  return { label: 'anchor', cls: 'bg-primary-100 text-primary-700' };
}

function SourceRow({
  s,
  isAnchor,
  productType,
}: {
  s: SelectedItem['sources'][number];
  isAnchor?: boolean;
  productType?: string | null;
}) {
  const substance = s.editorial_substance;
  const anchorStyle = anchorBadgeStyle(productType);
  return (
    <li className="flex items-start gap-2 text-xs">
      <span
        className={`flex-shrink-0 inline-flex justify-center items-center px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
          isAnchor ? anchorStyle.cls : 'bg-grey-200 text-grey-700'
        }`}
      >
        {isAnchor ? anchorStyle.label : s.role || 'source'}
      </span>
      <span className="flex-1 min-w-0">
        <span className="text-grey-800 line-clamp-1">{s.title}</span>
        <span className="text-[10px] text-grey-500 flex flex-wrap gap-2">
          {s.doc_form && <span className="font-mono">{s.doc_form}</span>}
          {substance && (
            <span
              className={`px-1 rounded ${substanceBadgeClass(substance)}`}
              title={SUBSTANCE_LABEL[substance]}
            >
              substance {substance}
            </span>
          )}
          {s.activity_sub_type && <span>{s.activity_sub_type}</span>}
        </span>
      </span>
    </li>
  );
}
